import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Download,
  Headphones,
  PhoneCall,
  RefreshCw,
  Server,
  UserRoundCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, type OperationCallDto } from '@/lib/api';
import { Page } from '@/components/AppShell';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input, LoadingState, Select, Stat } from '@/components/ui/primitives';
import { formatDuration } from '@/lib/utils';

function formatTime(epoch: number | null | undefined, timeZone: string): string {
  if (!epoch) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(epoch * 1000));
}

function formatPhone(value: string | null): string {
  if (!value) return '—';
  const digits = value.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  if (digits.length !== 10) return value;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function statusVariant(status: 'ok' | 'warn' | 'down'): 'success' | 'warning' | 'danger' {
  return status === 'ok' ? 'success' : status === 'warn' ? 'warning' : 'danger';
}

export function OperationsPage() {
  const [selectedUserId, setSelectedUserId] = React.useState<number | undefined>();
  const [selectedDate, setSelectedDate] = React.useState('');
  const [recordingUrls, setRecordingUrls] = React.useState<Record<string, string>>({});
  const [loadingRecording, setLoadingRecording] = React.useState<string | null>(null);
  const [exportingDaily, setExportingDaily] = React.useState(false);

  const overview = useQuery({
    queryKey: ['operations-overview', selectedUserId, selectedDate],
    queryFn: () => api.operationsOverview(selectedUserId, selectedDate || undefined),
    refetchInterval: 30_000,
  });
  const reliability = useQuery({
    queryKey: ['operations-reliability'],
    queryFn: api.operationsReliability,
    refetchInterval: 30_000,
  });

  React.useEffect(() => {
    if (selectedUserId === undefined && overview.data?.selectedUserId) {
      setSelectedUserId(overview.data.selectedUserId);
    }
  }, [overview.data?.selectedUserId, selectedUserId]);

  React.useEffect(() => {
    if (!selectedDate && overview.data?.date) {
      setSelectedDate(overview.data.date);
    }
  }, [overview.data?.date, selectedDate]);

  const refresh = () => {
    void overview.refetch();
    void reliability.refetch();
  };

  const exportDaily = async () => {
    setExportingDaily(true);
    try {
      await api.exportOperationsDaily(
        overview.data?.selectedUserId ?? selectedUserId,
        selectedDate || overview.data?.date || undefined,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not export the daily VA report');
    } finally {
      setExportingDaily(false);
    }
  };

  const loadRecording = async (call: OperationCallDto) => {
    if (!call.recordingId) return;
    setLoadingRecording(call.recordingId);
    try {
      const result = await api.operationRecording(call.recordingId);
      setRecordingUrls((current) => ({ ...current, [call.recordingId!]: result.url }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not load recording');
    } finally {
      setLoadingRecording(null);
    }
  };

  if (overview.isLoading) return <Page title="Operations"><LoadingState /></Page>;
  const data = overview.data;
  const summary = data?.summary;
  const shift = data?.shift;
  const calls = data?.calls;

  return (
    <Page
      title="Operations"
      description="VA activity, real Telnyx call data, recordings, call quality and LeadForge reliability in one place."
      actions={
        <div className="flex items-center gap-2">
          {data?.users.length ? (
            <Select
              value={String(data.selectedUserId ?? '')}
              onChange={(event) => setSelectedUserId(Number(event.target.value))}
              className="w-56"
            >
              {data.users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName}{user.isActive ? '' : ' (inactive)'}
                </option>
              ))}
            </Select>
          ) : null}
          <Input
            type="date"
            aria-label="Operations date"
            value={selectedDate || data?.date || ''}
            onChange={(event) => setSelectedDate(event.target.value)}
            className="w-40"
          />
          <Button variant="outline" size="sm" loading={exportingDaily} disabled={!data?.selectedUserId} onClick={() => void exportDaily()}>
            <Download className="h-3.5 w-3.5" />
            Export daily CSV
          </Button>
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
        </div>
      }
    >
      <div className="space-y-6 p-5 md:p-6">
        {summary ? (
          <section>
            <div className="mb-2 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-extrabold">{summary.displayName} · {data.isToday ? 'Today' : data.date}</h2>
                <p className="text-xs text-muted-foreground">{data.date} · {data.timezone}</p>
              </div>
              <Badge variant={summary.lastActivityAt && Date.now() / 1000 - summary.lastActivityAt < 600 ? 'success' : 'outline'}>
                Last activity {formatTime(summary.lastActivityAt, data.timezone)}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
              <Stat label="Logged in" value={formatTime(summary.loggedInAt, data.timezone)} />
              <Stat label="Leads viewed" value={summary.leadsViewed.toLocaleString()} />
              <Stat label="Calls attempted" value={summary.callsAttempted.toLocaleString()} />
              <Stat label="Connected" value={summary.callsConnected.toLocaleString()} />
              <Stat label="Talk time" value={formatDuration(summary.talkTimeSeconds)} />
              <Stat label="No Answer" value={summary.noAnswer.toLocaleString()} />
              <Stat label="Callbacks" value={summary.callbacks.toLocaleString()} />
              <Stat label="Not Interested" value={summary.notInterested.toLocaleString()} />
              <Stat label="Sold" value={summary.sold.toLocaleString()} />
              <Stat label="Bad Lead" value={summary.badLead.toLocaleString()} />
              <Stat label="Notes added" value={summary.notesAdded.toLocaleString()} />
              <Stat label="Follow-ups" value={summary.followUpsCreated.toLocaleString()} />
              <Stat label="Last activity" value={formatTime(summary.lastActivityAt, data.timezone)} />
            </div>
          </section>
        ) : (
          <Card><CardBody><p className="text-sm text-muted-foreground">No VA accounts found.</p></CardBody></Card>
        )}

        {shift && (
          <Card>
            <CardHeader>
              <CardTitle><Clock3 className="mr-2 inline h-4 w-4" />Shift productivity</CardTitle>
              <span className="text-2xs text-muted-foreground">Activity gaps mean no LeadForge/call event was recorded; they do not prove the VA was idle.</span>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
                <Stat label="First activity" value={formatTime(shift.firstActivityAt, data?.timezone ?? 'America/New_York')} />
                <Stat label="Last actual activity" value={formatTime(shift.lastActivityAt, data?.timezone ?? 'America/New_York')} />
                <Stat label="Activity span" value={formatDuration(shift.spanSeconds)} />
                <Stat label="Calls / shift hr" value={shift.callsPerShiftHour.toFixed(1)} />
                <Stat label="Leads / shift hr" value={shift.leadsPerShiftHour.toFixed(1)} />
                <Stat label="Gaps >15m" value={shift.activityGapsOver15m.toLocaleString()} />
                <Stat label="Longest gap" value={formatDuration(shift.longestActivityGapSeconds)} />
                <Stat
                  label="Callback completion"
                  value={shift.callbackCompletionPercent === null ? '—' : Math.round(shift.callbackCompletionPercent).toString() + '%'}
                  hint={
                    data?.isToday
                      ? shift.callbackTasksCompleted + ' completed · ' + shift.callbackTasksOutstanding + ' outstanding'
                      : shift.callbackTasksCompleted + ' completed that day · ' + shift.callbackTasksOutstanding + ' outstanding now due by that date'
                  }
                />
              </div>
            </CardBody>
          </Card>
        )}

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(25rem,0.85fr)]">
          <Card>
            <CardHeader>
              <CardTitle><Activity className="mr-2 inline h-4 w-4" />Activity timeline</CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              <div className="max-h-[34rem] overflow-y-auto">
                {data?.timeline.length ? data.timeline.map((entry) => (
                  <div key={entry.key} className="flex gap-3 border-b border-border/60 px-4 py-3 last:border-0">
                    <div className="w-24 shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">
                      {formatTime(entry.at, data.timezone)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold">{entry.summary}</p>
                      {entry.businessName && <p className="mt-0.5 truncate text-2xs text-muted-foreground">{entry.businessName}</p>}
                    </div>
                  </div>
                )) : <p className="p-4 text-xs text-muted-foreground">No activity recorded today yet.</p>}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle><Server className="mr-2 inline h-4 w-4" />Reliability</CardTitle>
              {reliability.data && <Badge variant={statusVariant(reliability.data.overall)}>{reliability.data.overall.toUpperCase()}</Badge>}
            </CardHeader>
            <CardBody className="space-y-2">
              {reliability.isLoading && <LoadingState />}
              {reliability.data?.checks.map((check) => (
                <div key={check.key} className="flex items-start gap-3 rounded-lg border border-border/70 p-3">
                  {check.status === 'ok' ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  ) : (
                    <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${check.status === 'down' ? 'text-destructive' : 'text-amber-500'}`} />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-bold">{check.label}</p>
                      <Badge variant={statusVariant(check.status)}>{check.status}</Badge>
                    </div>
                    <p className="mt-1 break-words text-2xs text-muted-foreground">{check.detail}</p>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        </div>

        {calls && (
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-extrabold">Admin call dashboard</h2>
              {calls.providerError && <Badge variant="danger">Telnyx feed: {calls.providerError}</Badge>}
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
              <Stat label={data.isToday ? 'Calls today' : 'Calls'} value={calls.callsToday.toLocaleString()} />
              <Stat label="Connected" value={`${Math.round(calls.connectedPercent * 100)}%`} hint={`${calls.connected} calls`} />
              <Stat label="Avg talk" value={formatDuration(calls.averageTalkTimeSeconds)} />
              <Stat label="Talk time" value={formatDuration(calls.talkTimeSeconds)} />
              <Stat label="Leads processed" value={calls.leadsProcessed.toLocaleString()} />
              <Stat label="Callbacks due" value={calls.callbacksDue.toLocaleString()} />
              <Stat label="Failed calls" value={calls.failedCalls.toLocaleString()} valueClassName={calls.failedCalls ? 'text-amber-300' : undefined} />
              <Stat label="Quality flags" value={calls.qualityFailures.toLocaleString()} valueClassName={calls.qualityFailures ? 'text-amber-300' : undefined} />
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <Card>
                <CardHeader><CardTitle>Outcomes</CardTitle></CardHeader>
                <CardBody className="space-y-2">
                  {calls.outcomes.length ? calls.outcomes.map((row) => (
                    <div key={row.name} className="flex items-center justify-between text-xs">
                      <span>{row.name}</span><span className="font-bold tabular-nums">{row.count}</span>
                    </div>
                  )) : <p className="text-xs text-muted-foreground">No saved dispositions today.</p>}
                </CardBody>
              </Card>
              <Card>
                <CardHeader><CardTitle>Calls per hour</CardTitle></CardHeader>
                <CardBody className="space-y-2">
                  {calls.callsPerHour.length ? calls.callsPerHour.map((row) => (
                    <div key={row.hour} className="flex items-center gap-3 text-xs">
                      <span className="w-16 text-muted-foreground">{row.hour}</span>
                      <div className="h-2 flex-1 overflow-hidden rounded bg-muted">
                        <div className="h-full bg-primary/70" style={{ width: `${Math.min(100, row.count * 10)}%` }} />
                      </div>
                      <span className="w-8 text-right font-bold tabular-nums">{row.count}</span>
                    </div>
                  )) : <p className="text-xs text-muted-foreground">No calls yet.</p>}
                </CardBody>
              </Card>
            </div>

            <Card>
              <CardHeader><CardTitle><PhoneCall className="mr-2 inline h-4 w-4" />Call history & recordings</CardTitle></CardHeader>
              <CardBody className="overflow-x-auto p-0">
                <table className="w-full min-w-[76rem] text-left text-xs">
                  <thead className="border-b border-border bg-muted/30 text-2xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">Time</th>
                      <th className="px-3 py-2">Lead</th>
                      <th className="px-3 py-2">Number used</th>
                      <th className="px-3 py-2">Destination</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Duration</th>
                      <th className="px-3 py-2">Outcome</th>
                      <th className="px-3 py-2">Quality</th>
                      <th className="px-3 py-2">Telnyx ID</th>
                      <th className="px-3 py-2">Recording</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calls.recent.map((call) => {
                      const recordingUrl = call.recordingId ? recordingUrls[call.recordingId] : undefined;
                      return (
                        <tr key={call.key} className="border-b border-border/60 align-top last:border-0">
                          <td className="whitespace-nowrap px-3 py-3 tabular-nums">{formatTime(call.initiatedAt, data.timezone)}</td>
                          <td className="max-w-52 px-3 py-3 font-semibold">{call.lead?.businessName ?? 'Unknown lead'}</td>
                          <td className="whitespace-nowrap px-3 py-3">{formatPhone(call.from)}</td>
                          <td className="whitespace-nowrap px-3 py-3">{formatPhone(call.to)}</td>
                          <td className="px-3 py-3"><Badge variant={call.connected ? 'success' : 'warning'}>{call.connected ? 'Connected' : 'Not connected'}</Badge></td>
                          <td className="whitespace-nowrap px-3 py-3">{formatDuration(call.durationSeconds)}</td>
                          <td className="px-3 py-3">{call.disposition?.outcome ?? '—'}</td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-1.5">
                              <Badge variant={call.qualityIssue ? 'warning' : 'success'}>{call.qualityIssue ? 'Review' : 'Good'}</Badge>
                              <span className="whitespace-nowrap text-2xs text-muted-foreground">
                                MOS {call.quality.mos?.toFixed(2) ?? '—'} · jitter {call.quality.jitterMaxVariance?.toFixed(1) ?? '—'}
                              </span>
                            </div>
                          </td>
                          <td className="max-w-44 px-3 py-3 font-mono text-[0.625rem] text-muted-foreground" title={call.callLegId ?? ''}>
                            {call.callLegId ? `${call.callLegId.slice(0, 8)}…` : '—'}
                          </td>
                          <td className="min-w-64 px-3 py-3">
                            {!call.recordingId ? (
                              <span className="text-2xs text-muted-foreground">No recording</span>
                            ) : recordingUrl ? (
                              <audio controls preload="none" src={recordingUrl} className="h-8 w-60" />
                            ) : (
                              <Button size="xs" variant="outline" loading={loadingRecording === call.recordingId} onClick={() => void loadRecording(call)}>
                                <Headphones className="h-3 w-3" /> Listen
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {!calls.recent.length && <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">No Telnyx calls today.</td></tr>}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </section>
        )}
      </div>
    </Page>
  );
}
